/**
 * Shared "this module is coming" page, used by the Solutions roadmap entries
 * (see `SolutionPlaceholders.tsx`). No `/modules/*` route mounts it any more —
 * the AI Agents and AI Tools stubs went when the real AI workspace shipped, and
 * Data Analytics went because it showed nothing. Per ADR-0004 a placeholder is
 * removed once its module lands. Centred card with the module name + a
 * one-line tagline
 * + the planned-phase reference. Phase numbering comes from
 * CLAUDE.md §4 so the page stays honest about what's next.
 *
 * Each placeholder also previews the role(s) the future module will
 * require — informational only, since the page itself is just behind
 * `RequireAuth` and any signed-in user can read it. When the caller's
 * roles do not include the required one, the page surfaces a Note
 * explaining how to acquire it (the answer: ask a tenant admin; users
 * cannot self-grant). The actual gate lands when the real module
 * routes ship.
 */

import type { ReactNode } from "react";
import { Alert, Badge, Card, Container, Group, Stack, Text, Title } from "@mantine/core";
import { IconClockHour4, IconLock } from "@tabler/icons-react";
import { useMe } from "@/features/users/useMe";

interface Props {
  name: string;
  tagline: string;
  phase: string;
  /** Roles that will be allowed to USE this module when it ships.
   *  Informational on the placeholder; enforced by the backend at the
   *  real endpoint when the module lands. */
  requiredRoles: readonly string[];
  /** Optional extra content under the notice — e.g. a pointer to the preview
   *  that exists today while the real module is being built. */
  children?: ReactNode;
}

export function ModulePlaceholder({ name, tagline, phase, requiredRoles, children }: Props) {
  const { data } = useMe();
  const callerRoles = data?.roles ?? [];
  const hasRequiredRole = requiredRoles.some((r) => callerRoles.includes(r));

  return (
    <Container size="md" py="xl">
      <Card withBorder radius="md" p="xl">
        <Stack gap="md">
          <Group justify="space-between" align="center">
            <Title order={2}>{name}</Title>
            <Badge variant="light" color="gray">
              {phase}
            </Badge>
          </Group>
          <Text c="dimmed">{tagline}</Text>

          <Group gap="xs" align="center" wrap="wrap">
            <Text size="sm" c="dimmed" tt="uppercase" fw={500}>
              Will require role
            </Text>
            {requiredRoles.map((r) => (
              <Badge key={r} color="brand" variant="outline">
                {r}
              </Badge>
            ))}
          </Group>

          {!hasRequiredRole && data && (
            <Alert
              color="yellow"
              variant="light"
              icon={<IconLock size={18} />}
              title="You won't have access"
            >
              <Group gap="xs" align="center" wrap="wrap">
                <Text size="sm" component="span">
                  Your current role{callerRoles.length === 1 ? "" : "s"}:
                </Text>
                {callerRoles.length === 0 ? (
                  <Text size="sm" c="dimmed" component="span">
                    (none)
                  </Text>
                ) : (
                  callerRoles.map((r) => (
                    <Badge key={r} color="gray" variant="light">
                      {r}
                    </Badge>
                  ))
                )}
              </Group>
              <Text size="sm" mt="xs">
                Ask a tenant administrator to grant the role above. The backend will enforce the
                gate when this module ships; the placeholder is informational.
              </Text>
            </Alert>
          )}

          <Alert
            variant="light"
            color="brand"
            icon={<IconClockHour4 size={18} />}
            title="Not yet built"
          >
            This module is on the product roadmap and isn't available yet.
          </Alert>

          {children}
        </Stack>
      </Card>
    </Container>
  );
}
